import { Link } from "react-router-dom";
import { ShieldCheck, XCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS, STORE_PHONE } from "@/constants/store";

export default function ReturnPolicyPage() {
  usePageMeta("Return & Exchange Policy",
    "Return, exchange, and store-credit policy for IPVALUE MARKETPLACE — a closeout & overstock retailer serving Grand Rapids and West Michigan from our Grand Rapids, MI showroom. 14-day store credit or exchange with receipt. No cash refunds.");
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-8 py-12" data-testid="policy-returns-page">
      <div className="ipv-eyebrow text-[#FFC220]">IPVALUE MARKETPLACE</div>
      <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 text-[#0A1C40]">
        Return &amp; Exchange Policy
      </h1>
      <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl">
        We are a closeout, overstock, and liquidation retailer. All items sell
        as-is. <b>Please inspect your item before you pay.</b>
      </p>

      {/* Two return models — Walmart/Best Buy marketplace style */}
      <section className="mt-8 grid md:grid-cols-2 gap-4" data-testid="policy-two-models">
        <div className="border-2 border-[#15803d] p-5">
          <div className="ipv-eyebrow font-black text-[#15803d]">Model A — In-store items</div>
          <p className="text-sm text-gray-800 mt-2 leading-relaxed">
            Items marked <b>"Available in store now"</b> follow the store policy below: 14-day exchange or{" "}
            <b>store credit</b>, receipt required, item unused in original packaging. No cash refunds. Bring the
            item, your receipt, and a valid photo ID to the store to start a return.
          </p>
        </div>
        <div className="border-2 border-[#0A1C40] p-5" data-testid="policy-model-b">
          <div className="ipv-eyebrow font-black text-[#0A1C40]">Model B — Ships-to-you items</div>
          <p className="text-sm text-gray-800 mt-2 leading-relaxed">
            Items marked <b>"Ships to you"</b> follow standard mail-return rules — like buying a marketplace
            item at a big-box store. Returns and exchanges are handled by mail, and refunds go
            back to your <b>original payment method</b>. <b>No store credit on shipped items.</b> Call{" "}
            <a href="tel:6164100158" className="underline font-bold">(616) 410-0158</a> with your order number and
            we'll start the return for you.
          </p>
        </div>
      </section>

      {/* 14-day rule */}
      <section className="mt-10 border-l-4 border-[#15803d] bg-[#f0fdf4] p-6" data-testid="policy-14day">
        <div className="flex items-center gap-2 text-[#15803d]">
          <CheckCircle2 className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Exchange or Store Credit — 14 Days</div>
        </div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p>You have <b>14 days from the day you receive your item</b> to bring it back for exchange or store credit. To qualify:</p>
          <ul className="ml-6 list-disc">
            <li>Receipt required</li>
            <li>Item must be <b>unused</b> and in original packaging</li>
            <li>Valid photo ID required</li>
          </ul>
          <p className="text-[#0A1C40] font-bold">
            We issue exchange or store credit only. We do NOT issue cash refunds.
          </p>
        </div>
      </section>

      {/* Final sale */}
      <section className="mt-8 border-l-4 border-[#DC2626] bg-red-50 p-6" data-testid="policy-final-sale">
        <div className="flex items-center gap-2 text-[#DC2626]">
          <XCircle className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Final Sale — No Returns or Exchanges</div>
        </div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <ul className="ml-6 list-disc space-y-1">
            <li>Mattresses, box springs, pillows, and all bedding (state health regulations)</li>
            <li>Anything tagged <b>AS-IS</b>, <b>CLEARANCE</b>, or <b>FINAL SALE</b></li>
            <li>Furniture that has been assembled or modified</li>
            <li>E-bikes and scooters <b>once ridden</b></li>
            <li>Personal care and beauty items</li>
          </ul>
        </div>
      </section>

      {/* Appliances / electronics */}
      <section className="mt-8 border-l-4 border-[#FFC220] bg-[#FFC220]/10 p-6" data-testid="policy-appliance-48hr">
        <div className="flex items-center gap-2 text-[#8a6a00]">
          <AlertTriangle className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Appliances &amp; Electronics — 48-Hour Test Rule</div>
        </div>
        <div className="mt-3 text-sm text-gray-800 leading-relaxed">
          <p>
            <b>Please test your item before you leave the store.</b> If an item
            does not work, tell us <b>within 48 hours</b> for exchange or store credit.
          </p>
        </div>
      </section>

      {/* Delivered items */}
      <section className="mt-8 border-l-4 border-gray-400 bg-gray-50 p-6" data-testid="policy-delivered-inspection">
        <div className="ipv-eyebrow font-black text-[#0A1C40]">Delivered Items</div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p>
            <b>Inspect your item and note any damage on the delivery receipt before our driver leaves.</b>
            Damage reported after the driver leaves cannot be accepted.
          </p>
          <p>
            Items returned by customer choice are refunded as store credit,
            <b> less the delivery fee</b>. Delivery fees are not refundable.
          </p>
        </div>
      </section>

      {/* Defective */}
      <section className="mt-8 border-l-4 border-[#0A1C40] bg-[#EEF3FF] p-6" data-testid="policy-defective">
        <div className="flex items-center gap-2 text-[#0A1C40]">
          <ShieldCheck className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Defective Merchandise</div>
        </div>
        <div className="mt-3 text-sm text-gray-800 leading-relaxed">
          <p>
            If an item is defective, <b>we will make it right regardless of the rules above</b>.
            Bring it back with your receipt and ask for a manager.
          </p>
        </div>
      </section>

      <div className="mt-10 text-sm text-gray-600">
        Questions? Come see us at <b>{STORE_ADDRESS.short}</b> or call <a className="underline font-bold text-[#0A1C40]" href={`tel:+1${STORE_PHONE.replace(/\D/g, "")}`}>{STORE_PHONE}</a>.
      </div>

      <div className="mt-8 text-center">
        <Link to="/shop" className="btn-gold inline-flex items-center gap-2" data-testid="policy-back-to-shop">
          Back to shopping
        </Link>
      </div>
    </div>
  );
}
